import axios from "../../axios";
import * as actionTypes from "../actions/types";
import { openEdifySpinner, getQuotationById, openMainModal, openSuscriptionModal, closeMainModal } from "./profileActions";
import { fireAnalytics } from "../../utils/firebase";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import { handleSubscriptionError } from "../../utils/subscriptionErrorHandler.ts";
import {
    putMaterialsQuotationAutofillDraft,
    publishMaterialsQuotationAutofillDraft,
    selectedMaterialsToAutofillMaterialLines,
    quoteFormToAutofillQuotationPatch,
} from "../../services/materialsQuotationAutofillDraftService.ts";
import {
    publishMaterialsQuotation,
    parseMaterialsQuotationPublishResponse,
    quoteFormToPublishQuotationBody,
    selectedMaterialsToPublishPayload,
} from "../../services/materialsQuotationPublishService.ts";

export const getCategories = () => async (dispatch) => {
    let authToken = localStorage.getItem("access-Token-Edify");
    let bearerToken = { headers: { Authorization: `Bearer ${authToken}` } };

    try {
        const res = await axios.get('/category/all', bearerToken);
        dispatch({
            type: actionTypes.GET_CATEGORY_LIST,
            payload: res.data,
        });

    } catch (err) {
        dispatch({
            type: actionTypes.GET_CATEGORY_ERROR,
            payload: err.message,
        });
    }
};


const dispatchMaterialQuotationPublished = (dispatch, getState, navigate, quotationId, analyticsExtra = {}) => {
    dispatch({ type: actionTypes.SEND_QUOTE_SUCCESS });
    const userData = getState().general.userData;
    fireAnalytics("material_quotation_created", {
        edify_user_id: userData?.id || "",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "publishing",
        action: "material_quotation_created",
        quotation_id: quotationId || "",
        quotation_type: "PRIVATE",
        ...analyticsExtra,
    });
    dispatch(
        openMainModal({
            title: "¡Tu pedido de materiales se ha publicado correctamente!",
            body: "Te notificaremos por correo electrónico y en la plataforma cuando los proveedores envíen un presupuesto para los materiales que has solicitado. ¡Gracias por confiar en nosotros!",
            isSuccess: true,
            confirmHandler: null,
            iconType: "success",
            noLabel: "Cerrar",
        })
    );
    if (quotationId) {
        setTimeout(() => {
            dispatch(getQuotationById(quotationId, false, null));
        }, 2000);
        setTimeout(() => {
            navigate(`/cotizaciondemateriales/${quotationId}`);
        }, 2000);
    }
};

const handleMaterialQuotationPublishError = (error, dispatch) => {
    dispatch(openEdifySpinner(false));
    dispatch({
        type: actionTypes.SEND_QUOTE_FAIL,
        payload:
            error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : error.message,
    });
    const handled = handleSubscriptionError(error, dispatch, openSuscriptionModal, openMainModal, closeMainModal);
    if (!handled) {
        const apiMessage =
            error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : null;
        dispatch(
            openMainModal({
                title: "¡Ups!",
                body:
                    apiMessage ||
                    "No hemos podido crear la solicitud, por favor inténtelo nuevamente o escribanos a contacto@edify.la ¡Muchas gracias!",
                isSuccess: true,
                confirmHandler: null,
                iconType: "error",
            })
        );
    }
    console.error("Error al publicar cotización de materiales:", error);
};

export const sendQuote = (quoteData, materials, navigate) => async (dispatch, getState) => {
    dispatch({ type: actionTypes.SEND_QUOTE_REQUEST });
    dispatch(openEdifySpinner(true));

    const materialsPayload = selectedMaterialsToPublishPayload(materials);
    if (materialsPayload.length === 0) {
        dispatch(openEdifySpinner(false));
        dispatch({
            type: actionTypes.SEND_QUOTE_FAIL,
            payload: "Debe incluir al menos un material",
        });
        dispatch(
            openMainModal({
                title: "¡Ups!",
                body: "Agregá al menos un material a la lista antes de publicar la cotización.",
                isSuccess: true,
                confirmHandler: null,
                iconType: "error",
            })
        );
        return;
    }

    try {
        const quotation = quoteFormToPublishQuotationBody({
            name: quoteData.name,
            city: quoteData.city,
            receiveFrom: quoteData.receiveFrom,
            receiveTo: quoteData.receiveTo,
            currency: quoteData.currency,
            description: quoteData.description,
            jurisdiccion: quoteData.jurisdiccion,
            delivery: quoteData.delivery === true,
            latitude: quoteData.latitude,
            longitude: quoteData.longitude,
        });
        const pub = await publishMaterialsQuotation(quotation, materialsPayload);
        dispatch(openEdifySpinner(false));
        const quotationId = parseMaterialsQuotationPublishResponse(pub);
        dispatchMaterialQuotationPublished(dispatch, getState, navigate, quotationId);
    } catch (error) {
        handleMaterialQuotationPublishError(error, dispatch);
    }
};

const parsePublishedQuotationIdFromAutofill = (pub) =>
    parseMaterialsQuotationPublishResponse(pub);

export const publishQuoteFromAutofillDraft =
    (draftId, quoteData, materials, categoryRows, navigate) => async (dispatch, getState) => {
        dispatch({ type: actionTypes.SEND_QUOTE_REQUEST });
        dispatch(openEdifySpinner(true));
        const cats = Array.isArray(categoryRows)
            ? categoryRows.map((c) => ({ id: c.id, name: c.name }))
            : [];
        try {
            const quotation = quoteFormToAutofillQuotationPatch({
                name: quoteData.name,
                city: quoteData.city,
                receiveFrom: quoteData.receiveFrom,
                receiveTo: quoteData.receiveTo,
                currency: quoteData.currency,
                description: quoteData.description,
                jurisdiccion: quoteData.jurisdiccion ? String(quoteData.jurisdiccion) : "",
                delivery: quoteData.delivery === true,
                latitude: quoteData.latitude,
                longitude: quoteData.longitude,
            });
            const materialLines = selectedMaterialsToAutofillMaterialLines(materials, cats);
            if (materialLines.length === 0) {
                throw Object.assign(new Error("No hay líneas válidas para publicar"), {
                    response: {
                        data: {
                            message:
                                "No hay líneas válidas para publicar. Revisá que los materiales tengan categoría o estén en el catálogo.",
                        },
                    },
                });
            }
            await putMaterialsQuotationAutofillDraft(draftId, { quotation, materialLines });
            const pub = await publishMaterialsQuotationAutofillDraft(draftId);
            dispatch(openEdifySpinner(false));
            const quotationId = parsePublishedQuotationIdFromAutofill(pub);
            dispatchMaterialQuotationPublished(dispatch, getState, navigate, quotationId, {
                source: "materials_autofill_draft",
            });
        } catch (error) {
            handleMaterialQuotationPublishError(error, dispatch);
        }
    };

export const getAllMaterialsCatalogFile = () => async (dispatch) => {
    let authToken = localStorage.getItem("access-Token-Edify");
    let bearerToken = { 
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: 'blob' 
    };

    try {
        dispatch(openEdifySpinner(true));
        const res = await axios.get('/materials/catalog/export', bearerToken);

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'catalogo.pdf'); 
        document.body.appendChild(link);
        link.click();


        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        dispatch(openEdifySpinner(false));
        dispatch(
            openMainModal({
                title: "¡Descarga exitosa!",
                body: "El archivo se ha descargado correctamente",
                iconType: "success",
                isSuccess: true,
                confirmHandler: null,
            })
        );

    } catch (err) {
        dispatch(openEdifySpinner(false));
        dispatch(
            openMainModal({
                title: "¡Ups!",
                body: "No hemos podido descargar el archivo, por favor inténtelo nuevamente o escribanos a contacto@edify.la ¡Muchas gracias!",
                isSuccess: true,
                iconType: "error",
                confirmHandler: null,
            })
        );  
    }
};


export const resetSuccess = () => ({
    type: actionTypes.RESET_SUCCESS,
});

